import {CustomSelect} from '../../../components/select/select.tsx';
import {Pill} from './pill';
import styles from './filters.module.scss';
import {useAppSelector} from '../../../store/store';
import {useBrands} from '../hooks/use_brands_hook';
import {useBuildYears} from '../hooks/use_build_years_hook';
import {useMileages} from '../hooks/use_mileages_hook';
import {GroupedModelOption, ModelOption, useModels} from '../hooks/use_models_hook';
import {usePrices} from '../hooks/use_prices_hook';
import {useTransmission} from '../hooks/use_transmission_hook';
import {useState} from 'react';
import {FlexRow} from '../../../components/utility/flex_row.tsx';
import {Alignment} from '../../../enums/alignment.ts';
import {Grid} from '../../../components/utility/grid.tsx';
import {Spacer} from '../../../components/utility/spacer.tsx';
import {AltButton} from '../../../components/button/alt_button.tsx';
import {__} from '../../../support/lang/translations.ts';
import {useLocations} from '../hooks/use_location_hook.ts';

export interface Filters {
    brands: string[] | undefined;
    models: string[] | undefined;
    transmission: string | undefined;
    buildYearFrom: number | undefined;
    buildYearTill: number | undefined;
    priceFrom: number | undefined;
    priceTill: number | undefined;
    mileageFrom: number | undefined;
    mileageTill: number | undefined;
    locationId: number | undefined;
}

interface OwnProps {
    filters: Filters;
    isLoading: boolean;
}

export function OccasionFilters({filters, isLoading}: OwnProps) {
    const occasionBrands = useAppSelector((state) => state.occasionBrands.occasionBrands);
    const locations = useAppSelector((state) => state.appConfig.locations);

    const {occasionBrandOptions, selectedOccasionBrandOption, onSetBrandsCallback} = useBrands(
        occasionBrands,
        filters.brands,
    );
    const {modelOptions, selectedModels, onSetModelsCallback, onClearModelCallback} = useModels(
        occasionBrands,
        filters.models,
        selectedOccasionBrandOption,
    );
    const {transmissionOptions, selectedTransmissionOptions, onSetTransmissionCallback} = useTransmission(
        filters.transmission,
    );

    const {locationOptions, selectedLocationOption, onSetLocationCallback} = useLocations(
        locations,
        filters.locationId,
    );

    const {
        buildYearFromOptions,
        buildYearTillOptions,
        selectedBuildYearFromOption,
        selectedBuildYearTillOption,
        onSetBuildYearFromCallback,
        onSetBuildYearTillCallback,
    } = useBuildYears(filters.buildYearFrom, filters.buildYearTill);

    const {
        mileageFromOptions,
        mileageTillOptions,
        selectedMileageFromOption,
        selectedMileageTillOption,
        onSetMileageFromCallback,
        onSetMileageTillCallback,
    } = useMileages(filters.mileageFrom, filters.mileageTill);

    const {
        priceFromOptions,
        priceTillOptions,
        selectedPriceFromOption,
        selectedPriceTillOption,
        onSetPriceFromCallback,
        onSetPriceTillCallback,
    } = usePrices(filters.priceFrom, filters.priceTill);

    const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(
        filters.transmission != null ||
            filters.buildYearFrom != null ||
            filters.buildYearTill != null ||
            filters.priceFrom != null ||
            filters.priceTill != null ||
            filters.mileageFrom != null ||
            filters.mileageTill != null ||
            filters.locationId != null,
    );

    return (
        <div className={styles.occasion_filters}>
            <div className={styles.dropdowns}>
                <Grid>
                    <label>
                        <CustomSelect
                            placeholder="Merk"
                            isClearable
                            value={selectedOccasionBrandOption}
                            options={occasionBrandOptions}
                            onChange={onSetBrandsCallback}
                            isDisabled={isLoading}
                            isLocked={false}
                            isSearchable={true}
                        />
                    </label>

                    <CustomSelect<ModelOption, true, GroupedModelOption>
                        placeholder="Model"
                        isMulti
                        isClearable
                        value={selectedModels}
                        options={modelOptions}
                        onChange={onSetModelsCallback}
                        isDisabled={isLoading}
                        isLocked={false}
                        isSearchable={true}
                    />
                </Grid>

                <div
                    className={`${isAdvancedFiltersOpen ? styles.advanced_filters_open : styles.advanced_filters_closed}`}
                >
                    <Spacer />
                    <Grid columns={locationOptions.length > 1 ? 2 : 1}>
                        <CustomSelect
                            placeholder="Transmissie"
                            isClearable
                            value={selectedTransmissionOptions}
                            options={transmissionOptions}
                            onChange={onSetTransmissionCallback}
                            isDisabled={isLoading}
                            isLocked={false}
                        />
                        {locationOptions.length > 1 ? (
                            <CustomSelect
                                placeholder="Vestiging"
                                isClearable
                                value={selectedLocationOption}
                                options={locationOptions}
                                onChange={onSetLocationCallback}
                                isDisabled={isLoading}
                                isLocked={false}
                            />
                        ) : null}
                    </Grid>
                    <Spacer />
                    <Grid>
                        <CustomSelect
                            placeholder="Bouwjaar v.a."
                            isClearable
                            value={selectedBuildYearFromOption}
                            options={buildYearFromOptions}
                            onChange={onSetBuildYearFromCallback}
                            isDisabled={isLoading}
                            isLocked={false}
                        />

                        <CustomSelect
                            placeholder="Bouwjaar tot"
                            isClearable
                            value={selectedBuildYearTillOption}
                            options={buildYearTillOptions}
                            onChange={onSetBuildYearTillCallback}
                            isDisabled={isLoading}
                            isLocked={false}
                        />

                        <CustomSelect
                            placeholder="Kilometerstand v.a."
                            isClearable
                            value={selectedMileageFromOption}
                            options={mileageFromOptions}
                            onChange={onSetMileageFromCallback}
                            isDisabled={isLoading}
                            isLocked={false}
                        />

                        <CustomSelect
                            placeholder="Kilometerstand tot"
                            isClearable
                            value={selectedMileageTillOption}
                            options={mileageTillOptions}
                            onChange={onSetMileageTillCallback}
                            isDisabled={isLoading}
                            isLocked={false}
                        />

                        <CustomSelect
                            placeholder="Prijs v.a."
                            isClearable
                            value={selectedPriceFromOption}
                            options={priceFromOptions}
                            onChange={onSetPriceFromCallback}
                            isDisabled={isLoading}
                            isLocked={false}
                        />

                        <CustomSelect
                            placeholder="Prijs tot"
                            isClearable
                            value={selectedPriceTillOption}
                            options={priceTillOptions}
                            onChange={onSetPriceTillCallback}
                            isDisabled={isLoading}
                            isLocked={false}
                        />
                    </Grid>
                </div>
            </div>

            <div className={styles.pills}>
                {filters.brands &&
                    filters.brands.length > 0 &&
                    filters.brands.map((brand) => (
                        <Pill
                            key={`brand_${brand}`}
                            name={brand}
                            onDeleteClick={() => onSetBrandsCallback(null)}
                            disabled={isLoading}
                        />
                    ))}
                {filters.models &&
                    filters.models.length > 0 &&
                    filters.models.map((model) => (
                        <Pill
                            key={`model_${model}`}
                            name={model}
                            onDeleteClick={() => onClearModelCallback(model)}
                            disabled={isLoading}
                        />
                    ))}
                {filters.transmission != null && (
                    <Pill
                        name={filters.transmission}
                        onDeleteClick={() => onSetTransmissionCallback(null)}
                        disabled={isLoading}
                    />
                )}
                {filters.buildYearFrom != null && (
                    <Pill
                        name={`Bouwjaar v.a. ${filters.buildYearFrom}`}
                        onDeleteClick={() => onSetBuildYearFromCallback(null)}
                        disabled={isLoading}
                    />
                )}
                {filters.buildYearTill != null && (
                    <Pill
                        name={`Bouwjaar tot ${filters.buildYearTill}`}
                        onDeleteClick={() => onSetBuildYearTillCallback(null)}
                        disabled={isLoading}
                    />
                )}
                {filters.priceFrom != null && (
                    <Pill
                        name={`Prijs v.a. ${filters.priceFrom}`}
                        onDeleteClick={() => onSetPriceFromCallback(null)}
                        disabled={isLoading}
                    />
                )}
                {filters.priceTill != null && (
                    <Pill
                        name={`Prijs tot ${filters.priceTill}`}
                        onDeleteClick={() => onSetPriceTillCallback(null)}
                        disabled={isLoading}
                    />
                )}
                {filters.mileageFrom != null && (
                    <Pill
                        name={`Km. stand v.a. ${filters.mileageFrom}`}
                        onDeleteClick={() => onSetMileageFromCallback(null)}
                        disabled={isLoading}
                    />
                )}
                {filters.mileageTill != null && (
                    <Pill
                        name={`Km. tot ${filters.mileageTill}`}
                        onDeleteClick={() => onSetMileageTillCallback(null)}
                        disabled={isLoading}
                    />
                )}
                {filters.locationId != null && (
                    <Pill
                        name={`Vestiging ${selectedLocationOption?.label}`}
                        onDeleteClick={() => onSetMileageTillCallback(null)}
                        disabled={isLoading}
                    />
                )}
            </div>

            <FlexRow alignment={Alignment.End}>
                <AltButton onClick={() => setIsAdvancedFiltersOpen((curr) => !curr)}>
                    <i className={isAdvancedFiltersOpen ? 'fa-light fa-close' : 'fa-light fa-filter'} />
                    <span>
                        {isAdvancedFiltersOpen
                            ? __('occasions.filter.buttons.close')
                            : __('occasions.filter.buttons.more')}
                    </span>
                </AltButton>
            </FlexRow>
        </div>
    );
}
